/**
 * Endpoints constants for Dashboards and Pivot Table API
 */

// ==================== COMMON CONSTANTS ====================

export const API_PREFIX = '/api';
export const PIVOT_BASE = 'pivot-table';
export const INSIGHTS_HUB_BASE = 'insights-hub';
export const REPORTING_SERVICE_BASE = 'reporting-service';

// ==================== DASHBOARDS ENDPOINTS ====================

// Dashboards endpoints (insights-hub - used in production, not Mint 2.0)
export const BFF_DASHBOARDS = 'bff/v1/dashboards/';
export const DASHBOARDS = `${INSIGHTS_HUB_BASE}/view/`;
export const DASHBOARDS_SETTINGS = `${INSIGHTS_HUB_BASE}/view/user/settings`;
export const DASHBOARDS_INSIGHTS_HUB_VIEW_BY_ID =
  `${INSIGHTS_HUB_BASE}/view/$viewId` as const;
export const DASHBOARDS_INSIGHTS_HUB_VIEW_BY_DASHBOARD_ID =
  `${INSIGHTS_HUB_BASE}/view/$dashboardId` as const;
export const DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID =
  `${INSIGHTS_HUB_BASE}/view/share/$viewId` as const;
export const WIDGET_DATA = `${INSIGHTS_HUB_BASE}/dashboard/graph`;

// Mint 2.0 Dashboards endpoints (insights-hub v2)
export const BFF_DASHBOARDS_V2 = 'bff/v2/dashboards/';
export const DASHBOARDS_V2_VIEW = `${INSIGHTS_HUB_BASE}/v2/view/`;
export const DASHBOARDS_V2_SETTINGS = `${INSIGHTS_HUB_BASE}/v2/view/user/settings`;
export const WIDGET_DATA_V2_MINT2 = `${INSIGHTS_HUB_BASE}/v2/dashboard/graph/`;
export const EXPORT_PIVOT_V2_MINT2 = `${INSIGHTS_HUB_BASE}/dashboard/graph/export/`;
export const EXPORT_CSV_V2_MINT2 = `${INSIGHTS_HUB_BASE}/v2/dashboard/graph/export_as_csv/`;
export const EXPORT_TABLE_FLOWCHART = `${INSIGHTS_HUB_BASE}/dashboard/flowchart-export/`;

// ==================== AG GRID SSRM ENDPOINTS ====================

export const GRID_DATA = `${REPORTING_SERVICE_BASE}/reports/grid/`;
export const GRID_DIMENSIONS = `${REPORTING_SERVICE_BASE}/dimensions/`;
export const GRID_METRICS = `${REPORTING_SERVICE_BASE}/metrics/`;

// ==================== PIVOT TABLE ENDPOINTS ====================

// Pivot Table endpoints (used in production, not Mint 2.0)
export const GET_TABLE_OUTDATED = `${PIVOT_BASE}/graph/v1`;
export const GET_TABLE = `${PIVOT_BASE}/graph/v2`;
export const GET_COMPARISON_TABLE_OUTDATED = `${PIVOT_BASE}/graph/v1/compare`;
export const GET_COMPARISON_TABLE = `${PIVOT_BASE}/graph/v2/compare`;
export const GET_REACH_SUBROWS = `${PIVOT_BASE}/reach/v1`;

// Pivot Table endpoints (for tests and direct API calls)
export const GET_METRICS = `${PIVOT_BASE}/metrics/v2/$companyId` as const;
export const GET_DIMENSIONS = `${PIVOT_BASE}/dimensions/v1`;
export const GET_DIMENSIONS_V2 =
  `${PIVOT_BASE}/dimensions/v2/$companyId` as const;
export const GET_USER_CURRENCIES =
  `${PIVOT_BASE}/graph/v1/currency/$companyId` as const;
export const GET_SELECTED_CURRENCY = 'currency/v1/user_currency/';
export const COMPANY_INFO = `${PIVOT_BASE}/company/info/v1/$companyId` as const;
export const DIMENSIONS_UNIQUES = `${PIVOT_BASE}/dimensions/uniques/v1`;

// Mint 2.0 Pivot Table endpoints
export const GET_TABLE_V2_MINT2 = `${PIVOT_BASE}/v2/graph/v2`;
export const GET_COMPARISON_TABLE_V2_MINT2 = `${PIVOT_BASE}/v2/graph/v2/compare`;
export const GET_METRICS_V2_MINT2 =
  `${PIVOT_BASE}/v2/metrics/v2/$companyId` as const;
export const GET_DIMENSIONS_V2_MINT2 =
  `${PIVOT_BASE}/v2/dimensions/v2/$companyId` as const;
export const GET_DIMENSIONS_UNIQUES_V2_MINT2 = `${PIVOT_BASE}/v2/dimensions/uniques/v1`;
export const GET_CURRENCIES_V2_MINT2 =
  `${PIVOT_BASE}/v2/currency/$companyId` as const;
export const COMPANY_INFO_V2_MINT2 =
  `${PIVOT_BASE}/v2/company/info/v1/$companyId` as const;
export const WIDGET_DATA_EXPORT = `${INSIGHTS_HUB_BASE}/dashboard/graph/export_as_csv`;
export const GET_FREQUENCY_SUBROWS = `${PIVOT_BASE}/frequency/v1`;

// ==================== TEST ENDPOINTS (with /api/ prefix) ====================
// These constants include the /api/ prefix for use in test mocks and direct API calls

export const API_BFF_DASHBOARDS = `${API_PREFIX}/${BFF_DASHBOARDS}`;
export const API_DASHBOARDS = `${API_PREFIX}/${DASHBOARDS}`;
export const API_DASHBOARDS_SETTINGS = `${API_PREFIX}/${DASHBOARDS_SETTINGS}`;
export const API_WIDGET_DATA = `${API_PREFIX}/${WIDGET_DATA}`;
export const API_GET_DIMENSIONS = `${API_PREFIX}/${PIVOT_BASE}/dimensions/v1`;
export const API_GET_DIMENSIONS_V2 = `${API_PREFIX}/${PIVOT_BASE}/dimensions/v2/:id`;
export const API_GET_METRICS = `${API_PREFIX}/${PIVOT_BASE}/metrics/v2/:id`;
export const API_GET_METRICS_COMPANY_ID = `${API_PREFIX}/${PIVOT_BASE}/metrics/v2/:companyId`;
export const API_GET_USER_CURRENCIES = `${API_PREFIX}/${PIVOT_BASE}/graph/v1/currency/:id`;
export const API_GET_USER_CURRENCIES_COMPANY_ID = `${API_PREFIX}/${PIVOT_BASE}/graph/v1/currency/:companyId`;
export const API_COMPANY_INFO = `${API_PREFIX}/${PIVOT_BASE}/company/info/v1/:id`;
export const API_DIMENSIONS_UNIQUES = `${API_PREFIX}/${PIVOT_BASE}/dimensions/uniques/v1`;
export const API_GET_TABLE = `${API_PREFIX}/${PIVOT_BASE}/graph/v2`;
export const API_GET_TABLE_OUTDATED = `${API_PREFIX}/${GET_TABLE_OUTDATED}`;
export const API_GET_COMPARISON_TABLE = `${API_PREFIX}/${PIVOT_BASE}/graph/v2/compare`;
export const API_GET_COMPARISON_TABLE_OUTDATED = `${API_PREFIX}/${GET_COMPARISON_TABLE_OUTDATED}`;
export const API_GET_REACH_SUBROWS = `${API_PREFIX}/${GET_REACH_SUBROWS}`;
export const API_GET_FREQUENCY_SUBROWS = `${API_PREFIX}/${GET_FREQUENCY_SUBROWS}`;
export const API_DASHBOARDS_INSIGHTS_HUB_VIEW_SHARE_BY_ID = `${API_PREFIX}/${INSIGHTS_HUB_BASE}/view/share/:viewId`;
export const API_GRID_DATA = `${API_PREFIX}/${GRID_DATA}`;
export const API_GRID_DIMENSIONS = `${API_PREFIX}/${GRID_DIMENSIONS}`;
export const API_GRID_METRICS = `${API_PREFIX}/${GRID_METRICS}`;
